import Controller from '@ember/controller';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';
import type {
  Group,
  Option,
} from '@mylighthouse/prism-ember/types/components/prism/combo-select-input';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { tKey } from 'ember-intl';
import {
  RI_BREAKDOWNS,
  type RiBreakdown,
} from 'frontend-utils/utils/constants/constant-values';
import type { SupportedCurrencies } from 'frontend-utils/utils/constants/currency-mapping-constants';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import Cookies from 'js-cookie';
import cloneDeep from 'lodash.clonedeep';
import type { PaceLeadtimeOption } from 'lumen/components/hotel-and-market/revenue-insight/dropdowns/chart-options-pace';
import type { SegmentationBlock } from 'lumen/components/hotel-and-market/revenue-insight/sort-and-reorder-blocks';
import ENV from 'lumen/config/environment';
import type { Period } from 'lumen/models';
import type { SegmentationValueLike } from 'lumen/models/revenue-insight/segmentation-value';
import type HotelRevenueInsightRoute from 'lumen/routes/hotel/revenue-insight';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import type { DateAggregationType } from 'lumen/utils/constants/general-constants';
import { KPI_DISPLAY_CUSTOM_MAPPING } from 'lumen/utils/constants/pms-mapping-constants';
import {
  ALL_SELECTABLE_SEGMENTATION_BLOCKS,
  CANCELLATION_ONLY_BLOCKS,
  type CancellationsView,
  DEFAULT_SEGMENTATION_BLOCKS_ORDER,
  FILTERABLE_SEGMENTATION_BLOCKS,
  type FilterableSegmentationBlock,
  INVALID_RESERVATIONS_MESSAGE,
  type KpiCategory,
  KPIS,
  PACE_LEADTIME_OPTIONS,
  TARGET_BLOCKS,
  type ValidKpi,
  type View,
  WEEKS_IN_YEAR,
} from 'lumen/utils/constants/ri-constant-values';
import {
  type ActiveFilter,
  addFilter,
  addFilters,
  addFiltersToActiveFilters,
  getParents,
  getSegmentationValue,
  hasAllSelected,
  hasFilter,
  mergeFilters,
  removeFilter,
  removeFilters,
  toggleFilter,
} from 'lumen/utils/filter-bar/filter-functions';
import { parseObject, stringifyObject } from 'lumen/utils/parse-utils';
import { formatUrl } from 'lumen/utils/rates-request';
import {
  addRatesToStayDates,
  createSegmentationBlockStructure,
  getSdlyEnd,
  type SegmentationBlockStructure,
  sortValues,
  toggleFilterMessageCookie,
} from 'lumen/utils/revenue-insight/controller-utils';
import generateSingleURL from 'lumen/utils/revenue-insight/generate-api-url';
import getDateRange from 'lumen/utils/revenue-insight/get-date-range';
import { isSameOrAfterExtractDate } from 'lumen/utils/revenue-insight/ri-utils';
import {
  segmentationIdFormatter,
  segmentationValueFormatter,
} from 'lumen/utils/revenue-insight/segmentation-value-formatter';
import type { ModelFrom } from 'lumen/utils/routes';
import { timeout } from 'lumen/utils/waiters';

const MESSAGE_DISPLAY_COOKIE = 'ota-hide-messages';
const NET_BOOKINGS_DISPLAY_COOKIE = 'ota-hide-net-bookings';
const NET_CANCELLATIONS_DISPLAY_COOKIE = 'ota-hide-net-cancellations';

// In some breakdowns we show different KPIs (e.g. pickup, pace and tooltips). These are the KPIs to replace.
const KPIS_TO_REPLACE = {
  [KPIS.OCCUPANCY]: KPIS.NR_OF_ROOMS,
  [KPIS.REVPAR]: KPIS.REVENUE,
};

const SETTINGS_OPTIONS = {
  BREAKDOWN_SETTINGS: 'breakdown-settings',
  SEGMENTATION_VIEW: 'segmentation-view',
  CENTRAL_MAPPING: 'central-mapping',
};

const DEFAULT_BREAKDOWNS = [
  RI_BREAKDOWNS.ALL,
  RI_BREAKDOWNS.IMPACT_DATE,
  RI_BREAKDOWNS.PACE,
  RI_BREAKDOWNS.PICKUP,
];

type ParsedFilters = Record<string, string[]>;

export default class HotelRevenueInsightController extends Controller {
  // @ts-expect-error: Incorrect type
  declare model: ModelFrom<HotelRevenueInsightRoute>;

  // @ts-expect-error: Property 'queryParams' in type 'HotelRevenueInsightController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    {
      kpi: { scope: 'controller' },
      kpiCategory: { scope: 'controller' },
      leadtime: { scope: 'controller' },
      roomsTab: { scope: 'controller' },
      revenueTab: { scope: 'controller' },
      // the actual segmentation value filters
      segmentationFilters: { scope: 'controller' },
      stayDateFilter: { scope: 'controller' },
      // Date related parameters
      fromDate: { scope: 'controller' },
      toDate: { scope: 'controller' },
      refFromDate: { scope: 'controller' },
      reference: { scope: 'controller' },
      extractDate: { scope: 'controller' },
      stayDateAggregation: { scope: 'controller' },
      selectedView: { scope: 'controller' },
      cancellationsView: { scope: 'controller' },
      scheduleReport: { scope: 'controller' },
      showPickupRealised: { scope: 'controller' },
      hidePickupLY: { scope: 'controller' },
      disableDataContinuity: { scope: 'controller' },
      currency: { scope: 'controller' },
      highlight: { scope: 'controller' },
      profile: { scope: 'controller' },
    },
  ] as const;

  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked activeFilters: ActiveFilter[] = [];
  @tracked cancellationsView: CancellationsView =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].cancellationsView;
  @tracked currency?: SupportedCurrencies;
  // Debug tool for data engineering
  @tracked disableDataContinuity = false;
  @tracked doNotDisplayNetBookingsInformationBanner = false;
  @tracked doNotDisplayNetCancellationsInformationBanner = false;
  @tracked doNotDisplayRIFilterMessage = false;
  @tracked exportChoices: Group<Option>[] = [];
  @tracked extractDate: string | null = null;
  @tracked fromDate: string | null = null;
  @tracked hasTrackedPickupPaceSectionView: boolean = false;
  // false = SHOW; toggling to hide (true) persists. refreshModel:true → full reload.
  @tracked hidePickupLY = false;
  @tracked isFilterBarHovered = false;
  @tracked isShowingKpiSectionModal = false;
  @tracked isShowingReviMappingTypeModal = false;
  @tracked isShowingSelectionAndSortModal = false;
  // Default values for URL parameters
  @tracked kpi: ValidKpi = DEFAULT_QP_VALUES.hotel['revenue-insight'].kpi;
  @tracked kpiCategory: KpiCategory =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].kpiCategory;
  @tracked leadtime: number =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].leadtime;
  @tracked pickupPaceSectionElement: HTMLElement | null = null;
  @tracked profile: string | null = null;
  @tracked reference = 'SWLY';
  @tracked refFromDate: string | null = null;
  @tracked revenueTab: ValidKpi =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].revenueTab;
  @tracked roomsTab: ValidKpi =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].roomsTab;
  @tracked scheduleReport = false;
  // URL parameter containing the JSON.stringified filters
  @tracked segmentationFilters = '';
  @tracked selectedView: View =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].selectedView;
  // Non-target pace toggle QP. Default OFF (absent from URL); pace-only refetch.
  @tracked showPickupRealised = false;

  @tracked stayDateAggregation: DateAggregationType =
    DEFAULT_QP_VALUES.hotel['revenue-insight'].stayDateAggregation;
  // Staydate filter is separate since it requires custom behaviour,
  @tracked stayDateFilter = '';
  @tracked toDate: string | null = null;

  // The owning route, handed in by its setupController (controllers can't
  // inject routes). Used to trigger a pace-only refetch.
  declare route: HotelRevenueInsightRoute;

  searchValuesPerSegmentationId: Record<
    FilterableSegmentationBlock,
    { isSearchActive: boolean; searchValue: string }
  >;

  segmentationFilterClickedTask = task(
    { enqueue: true },
    async (segmentationId, segmentationValueId): Promise<void> => {
      // Small timeout just to be able to enqueue this task
      // Makes the page a little more responsive
      await timeout('extra-short');

      // This code is called when the selected filters need to change. The segmentation object of the filter to be updated is passed as a parameter
      // Default behaviour: remove the filter if it's already present in the active filters / add the filter if it's not present in the active filters
      // Exceptions: pickup & pace filters

      // Note: use segmentationValueId as URL parameter.
      // In case of a very long segmentationValue, this will be a truncated version

      // This needs to be looked up here, so all of this state is not saved in the filter bar
      const segmentationObject = getSegmentationValue(
        this.sortedVisibleSegmentations,
        segmentationId,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        segmentationValueId,
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const currentFilters = parseObject(this.segmentationFilters);
      let children = null;
      let parents = null;
      let oneOfParentsIsActive = false;

      if (segmentationObject) {
        children = segmentationObject.children;
        parents = getParents(segmentationObject);
        oneOfParentsIsActive = parents.some((parent) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          currentFilters[parent.segmentationId as RiBreakdown]?.includes(
            parent.segmentationValueId,
          ),
        );
      }

      const { PACE, PICKUP, SUBSCRIPTION_ID } = RI_BREAKDOWNS;

      switch (segmentationId) {
        case PICKUP:
          this.usageAnalytics.track('Pick-up Filter Applied', {
            eventTrigger: 'pick-up-widget',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterValue: segmentationValueId,
          });
          break;
        case PACE: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const startDateString = segmentationValueId.split(':')[0];
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const endDateString = segmentationValueId.split(':')[1];

          const startDate =
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            this.hotelAndMarketService.toLocalTime(startDateString);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          const endDate = this.hotelAndMarketService.toLocalTime(endDateString);

          this.usageAnalytics.track('Pace Filter Applied', {
            eventTrigger: 'pace-widget',
            amountOfDays: endDate.diff(startDate, 'days'),
            startDaysOffsetFromToday: startDate.diff(
              this.hotelAndMarketService.getLocalTime(),
              'days',
            ),
          });
          break;
        }
        case SUBSCRIPTION_ID:
          this.usageAnalytics.track('Hotel Included Changed', {
            eventTrigger: 'hotel-widget',
          });
          break;
        default:
          this.usageAnalytics.track('Breakdown Filter Applied', {
            eventTrigger: 'breakdown-widget',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterType: segmentationId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            filterValue:
              segmentationObject?.segmentationValue ?? segmentationValueId,
          });
      }

      if (segmentationId === PACE || segmentationId === PICKUP) {
        // Specific behaviour for pace and pickup filters
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        if (hasFilter(segmentationId, segmentationValueId, currentFilters)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
          removeFilter(segmentationId, segmentationValueId, currentFilters);
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        } else if (segmentationId === PICKUP && currentFilters[PICKUP]) {
          // There can only be one filter active: remove the current filter if there is already one active
          // Extra edge case for pickup since this can be selected as well
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentFilters[segmentationId].indexOf(segmentationValueId) === -1
          ) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            currentFilters[segmentationId] = [segmentationValueId];
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            delete currentFilters[segmentationId];
          }
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          currentFilters[segmentationId] = [segmentationValueId];
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (segmentationId === PACE && currentFilters[PICKUP]) {
          // A pickup and pace filter can not be active at the same time. Remove any active pickup filters if there are any
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          delete currentFilters[PICKUP];
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (segmentationId === PICKUP && currentFilters[PACE]) {
          // A pickup and pace filter can not be active at the same time. Remove any active pace filters if there are any
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          delete currentFilters[PACE];
        }
      } else if (!oneOfParentsIsActive) {
        // Default behaviour for filters that are not pickup or pace
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument
        toggleFilter(segmentationId, segmentationValueId, currentFilters);
      }

      if (parents) {
        if (oneOfParentsIsActive) {
          const firstActiveParentIndex = parents.findIndex((parent) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            currentFilters[parent.segmentationId as string].includes(
              parent.segmentationValueId,
            ),
          );
          const activeParents = parents.splice(firstActiveParentIndex);

          this.handleActiveParent(
            activeParents,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            currentFilters,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            segmentationId,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            segmentationValueId,
          );
        }

        // Do the following check for all parents (in the order of bottom to top).
        parents.reverse().forEach((parent) => {
          // If you complete all children, the parent will be selected instead.
          if (
            parent.children &&
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            hasAllSelected(parent.children, currentFilters) &&
            parent.isSelectable &&
            parent.segmentationId &&
            parent.segmentationValueId
          ) {
            addFilter(
              parent.segmentationId,
              parent.segmentationValueId,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              currentFilters,
            );
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            removeFilters(parent.children, currentFilters);
          }
        });
      }

      // When selecting a parent, remove all of its children from the filters (including children of children, etc).
      if (children && children.length) {
        const nestedChildren =
          HotelRevenueInsightController.getAllNestedChildren(
            segmentationObject,
          );
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        removeFilters(nestedChildren, currentFilters);
      }

      // Commit whatever changes happend to parsedSegmentationFilters into the query params filter
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.setSegmentationFilters(stringifyObject(currentFilters));
    },
  );

  showUnavailableMessageTask = task({ drop: true }, async (): Promise<void> => {
    await timeout();
    this.toast.alert(
      this.intl.t('revenue_insight.filters.unavailable_message'),
      {
        showIcon: true,
      },
    );
  });

  @dependentKeyCompat
  get breakdowns(): Partial<Record<RiBreakdown, SegmentationValueLike[]>> {
    return this.model.breakdowns.last?.value ?? {};
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get breakdownSettingsOptions() {
    const options: Option[] = [
      {
        id: SETTINGS_OPTIONS.BREAKDOWN_SETTINGS,
        label: this.intl.t('revenue_insight.breakdown_settings.option'),
      },
    ];

    if (this.access.hasFeature('revenueinsight.pms.edit')) {
      if (this.isMappingTypeModalAvailable) {
        options.push({
          id: SETTINGS_OPTIONS.SEGMENTATION_VIEW,
          label: this.intl.t(
            'settings.central_mapping.temporary_mapping_type_settings.title',
          ),
        });
      }
      options.push({
        id: SETTINGS_OPTIONS.CENTRAL_MAPPING,
        label: this.intl.t(
          'revenue_insight.breakdown_settings.action.mapping_settings',
        ),
        unsafe_isLink: true,
      });
    }

    return options;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMaxDate() {
    return this.hotelAndMarketService.ratesDataEnd;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMinDate() {
    return this.hotelAndMarketService.toLocalTime(
      this.hotelAndMarketService.hotel?.dataContinuityFirstCompleteStayDateWithDefault.format(),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRangeOptions() {
    // When looking at a demo subscription we want to use the freeze date as today's date. Fall back on today if revenueInsightDemoFreezeDate is null
    const todayMoment = this.hotelAndMarketService.toLocalTimeWithFallback(
      this.hotel?.revenueInsightDemoFreezeDate?.format('YYYY-MM-DD'),
    );

    // All possible quick select options for the datepicker
    let options = [
      {
        text: 'revenue_insight.current_month',
        id: 'current_month',
        isSelected: true,
        range: {
          start: todayMoment.startOf('month'),
          end: todayMoment.endOf('month'),
        },
      },
      {
        text: 'revenue_insight.current_month_plus',
        count: 2,
        id: 'current_month_plus_2',
        isSelected: false,
        range: {
          start: todayMoment.startOf('month'),
          end: todayMoment.add(2, 'month').endOf('month'),
        },
      },
      {
        text: 'revenue_insight.current_quarter',
        id: 'quarter',
        isSelected: false,
        range: {
          start: todayMoment.startOf('quarter'),
          end: todayMoment.endOf('quarter'),
        },
      },
      {
        text: 'general.next_days',
        count: 90,
        id: 'ninety_days',
        isSelected: false,
        range: {
          start: todayMoment,
          end: todayMoment.add(90, 'day'),
        },
      },
      {
        text: 'revenue_insight.year_to_date',
        id: 'year_to_date',
        isSelected: false,
        range: {
          start: todayMoment.startOf('year'),
          end: todayMoment,
        },
      },
      {
        text: 'revenue_insight.full_year',
        id: 'full_year',
        isSelected: false,
        range: {
          start: todayMoment.startOf('year'),
          end: todayMoment.endOf('year'),
        },
      },
      {
        text: 'general.custom',
        id: 'custom',
        isSelected: false,
        range: {
          // Made custom start today in case the integration date is after the startof month
          start: this.fromDateMoment,
          end: this.fromDateMoment.add(this.referencePeriod, 'day'),
        },
      },
    ];

    // Filter out all of the options which start before the integration date
    options = options.filter(
      (option) => option.range.start >= this.datePickerMinDate,
    );

    return [
      {
        id: 'references',
        title: this.intl.t('revenue_insight.report_selected_period').toString(),
        options,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRefMaxDate() {
    return this.fromDateMoment.subtract(1, 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRefMinDate() {
    return this.hotelAndMarketService.ratesDataStart;
  }

  // Segmentation block expects sorted segmentations, so we'll need to sort this as well
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get extraSortOptions() {
    const { bobSortKey } = this.riSelections;
    return {
      sortType: bobSortKey,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get favourites() {
    return this.hotelOrMarket.favouriteFilters.slice();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterDisplayNameOverride() {
    // For RI we do the display name formatting based on segmentationValue instead of segmentationValueId
    // Create a mapping that can be used to lookup the value of a value_id
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filters: Record<RiBreakdown, string[]> =
      parseObject(this.segmentationFilters) ?? {};
    const filterDisplayNameOverride: Partial<
      Record<RiBreakdown, Record<string, string>>
    > = {};
    (Object.keys(filters) as RiBreakdown[]).forEach((filterKey) => {
      // In MP we can filter on hotels, this would put an entry for subscription_id in the dict.
      // We don't need display names for that, only for breakdowns.
      if (Object.values(RI_BREAKDOWNS).includes(filterKey)) {
        filterDisplayNameOverride[filterKey] =
          this.buildSegmentDisplayNameOverrideForBreakdown(
            filterKey,
            filters[filterKey],
          );
      }
    });

    return filterDisplayNameOverride;
  }

  /**
   * This CP determines if we can still filter breakdowns
   * It return an object that has counts how many filters you can still click in that breakdown
   * example:
   * {
   *   travel_agent: 5,
   *   pickup: 0
   * }
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filterLimits() {
    return FILTERABLE_SEGMENTATION_BLOCKS.reduce(
      (limits, blockId) => {
        if (
          this.riSelections.isTargetActive &&
          blockId !== RI_BREAKDOWNS.IMPACT_DATE
        ) {
          // No filters available if you are looking at budget or forecast
          limits[blockId] = 0;
        } else if (
          blockId === RI_BREAKDOWNS.PICKUP &&
          this.riSelections.isCancellationsPercentageView
        ) {
          // No filtering for recent cancellations
          limits[blockId] = 0;
        } else if (
          blockId === RI_BREAKDOWNS.PACE &&
          this.riSelections.isCancellationsPercentageView
        ) {
          // No filtering for recent cancellations
          limits[blockId] = 0;
        } else {
          // Otherwise you have an unlimited amount of filters
          limits[blockId] = Infinity;
        }

        return limits;
      },
      {} as Record<FilterableSegmentationBlock, number>,
    );
  }

  /**
   * Checks if any segmentation filter is active
   * @return { boolean }   : return if any filter is active
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filtersActive() {
    return this.segmentationFilters.length > 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    return typeof this.riSelections.defaultExtractDate === 'string'
      ? this.riSelections.defaultExtractDate
      : this.riSelections.defaultExtractDate.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedUpdateTimestamp() {
    return this.updateTimestamp?.fromNow();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get fromDateMoment() {
    return this.fromDate
      ? this.hotelAndMarketService.toLocalTime(this.fromDate)
      : this.hotelAndMarketService.getLocalTime().startOf('month');
  }

  get hasCustomCurrencyFeatureEnabled(): boolean {
    return this.access.hasCurrencyConversionViewAccess;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasKPISettingsAccess() {
    return this.access.hasFeature('revenueinsight.kpi_display_edit');
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get intercomLink() {
    return {
      text: this.intl.t('general.learn_more'),
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      action: () =>
        this.intercom.redirectToIntercomHelp('en/articles/6949233-what-is-net'),
      icon: true,
    };
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isClosed() {
    return (
      this.hotel?.revenueInsightClosureDate &&
      this.hotel?.revenueInsightClosureDate.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    );
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDemoProperty() {
    return this.hotel?.isDemoProperty;
  }

  // SP: if selected item has a parent in the filters, remove the parent from filters and select all of it's children (except for the one you clicked on).
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFavouritesDisabled() {
    return this.riSelections.isTargetActive;
  }

  get isMappingTypeModalAvailable(): boolean {
    return !this.hotelAndMarketService.hotel?.spiderMarketId;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isModelLoading() {
    return (
      this.model.kpis.isRunning ||
      this.model.breakdowns.isRunning ||
      this.model.rates.isRunning ||
      this.model.pickup.isRunning ||
      // Pace counts here only on a model-wide load; a pace-only refetch has its
      // own card bar and must not trigger the page-wide loader.
      (this.model.pace.isRunning && !this.isPaceRefetching)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNetBookingsBannerVisible() {
    return (
      this.riSelections.isBookingsOnlyView &&
      this.riSelections.isNetComparison &&
      !this.doNotDisplayNetBookingsInformationBanner
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNetCancellationsBannerVisible() {
    return (
      this.riSelections.isCancellationsOnlyView &&
      this.riSelections.isNetComparison &&
      !this.doNotDisplayNetCancellationsInformationBanner
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isOutOfSync() {
    const timestamp = dayjs.duration(dayjs().diff(this.updateTimestamp));

    return timestamp?.asHours() > 24;
  }

  get isPaceFilterActive(): boolean {
    return Boolean(this.parsedSegmentationFilters['pace']);
  }

  // True during a pace-only refetch (chart kept via SWR) — drives the pace card bar.
  get isPaceRefetching(): boolean {
    return this.model.pace.isRunning && !this.showPaceLoadingSkeleton;
  }

  get isPickupFilterActive(): boolean {
    return Boolean(this.parsedSegmentationFilters['pickup']);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiBlocks() {
    const nrOfRoomsAndOccupancyBlock = {
      id: 'occupancy',
      kpis: [KPIS.OCCUPANCY, KPIS.NR_OF_ROOMS],
      activeTab: this.roomsTab,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
        // eslint-disable-next-line ember/no-side-effects
        this.roomsTab = kpi;
      },
    };

    const adrBlock = {
      id: 'adr',
      kpis: [KPIS.ADR],
      activeTab: KPIS.ADR,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
      },
    };

    const revenueAndRevparBlock = {
      id: 'revenue',
      kpis: [KPIS.REVENUE, KPIS.REVPAR],
      activeTab: this.revenueTab,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      changeTab: (kpi: ValidKpi) => {
        this.changeSelectedKpi(kpi);
        // eslint-disable-next-line ember/no-side-effects
        this.revenueTab = kpi;
      },
    };

    if (this.riSelections.isCancellationsPercentageView) {
      return [nrOfRoomsAndOccupancyBlock, revenueAndRevparBlock];
    }

    return [nrOfRoomsAndOccupancyBlock, adrBlock, revenueAndRevparBlock];
  }

  /**
   * This CP determines the actual kpi per breakdown.
   * We need it because impact_date and subscription_id are the only breakdowns where we want to show occupancy.
   * It returns an object that maps the correct kpi to the id.
   * Example:
   * {
   *   travel_agent: 'nrOfRooms',
   *   impact_date: 'occupancy'
   * }
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get kpiPerBreakdown() {
    const kpiPerBreakdown: Partial<Record<RiBreakdown, ValidKpi>> = {};

    FILTERABLE_SEGMENTATION_BLOCKS.forEach((blockId) => {
      kpiPerBreakdown[blockId] =
        KPIS_TO_REPLACE[this.kpi as keyof typeof KPIS_TO_REPLACE] ?? this.kpi;
    });

    kpiPerBreakdown[RI_BREAKDOWNS.IMPACT_DATE] = this.kpi;
    kpiPerBreakdown[RI_BREAKDOWNS.SUBSCRIPTION_ID] = this.kpi;

    return kpiPerBreakdown as Record<RiBreakdown, ValidKpi>;
  }

  @dependentKeyCompat
  get kpis(): Partial<Record<RiBreakdown, SegmentationValueLike[]>> {
    return this.model.kpis.last?.value ?? {};
  }

  /**
   * Maps rates to dates for easy lookup later on when merging them with stayDate data
   *
   * {
   *    2018-12-1: { 14059: ratesPerHotel, 20040: ..., },
   *    2018-12-2: ...,
   *    ...
   * }
   *
   * @return {Object} Object containing all rates mapped to stay dates
   */
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get mappedRates() {
    const mappedRates: Record<string, Period> = {};
    if (!this.model.rates.isRunning && this.rates.length) {
      this.rates.forEach((period) => {
        // @ts-expect-error: Type 'null' cannot be used as an index type.
        mappedRates[period.day.id] = period;
      });
    }

    return mappedRates;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfDaysSelected() {
    return this.fromDateMoment.diff(this.refFromDateMoment, 'day');
  }

  get pace(): SegmentationValueLike[] {
    // lastSuccessful keeps the previous series on screen through a refetch (in
    // flight or failed) instead of blanking — the error toast signals failure, and
    // blanking would hide the Chart-options dropdown and dead-end the card. Empty
    // only on the first load (no lastSuccessful yet).
    return this.model.pace.lastSuccessful?.value?.pace ?? [];
  }

  // Leadtime options are disabled whenever a pace/pickup filter defines the window.
  get paceChartOptionsDisabled(): boolean {
    return this.isPickupFilterActive || this.isPaceFilterActive;
  }

  // Whether the active pace filter's range extends to/past the extract date.
  get paceFilterIncludesFutureDates(): boolean {
    const paceFilter = this.parsedSegmentationFilters['pace']?.[0];
    const endDate = paceFilter?.split(':')[1];
    if (!endDate) {
      return false;
    }
    const extractDate = this.riSelections.defaultExtractDate;
    return isSameOrAfterExtractDate(
      endDate,
      typeof extractDate === 'string'
        ? extractDate
        : extractDate.format('YYYY-MM-DD'),
    );
  }

  // Leadtime options shown in the pace chart "Chart options" dropdown.
  get paceLeadtimeOptions(): PaceLeadtimeOption[] {
    return PACE_LEADTIME_OPTIONS.map((days) => ({
      id: days,
      label: this.intl.t('general.days', { count: days }).toString(),
    }));
  }

  // We parse the moment objects instead of using fromDate,toDate & refFromDate directly to make sure the defaults aren't null
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedFromDate() {
    return this.fromDateMoment.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedRefFromDate() {
    return this.refFromDateMoment.format('YYYY-MM-DD');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedSegmentationFilters() {
    return this.riSelections.parsedSegmentationFilters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedStayDateFilter() {
    return this.riSelections.parsedStayDateFilter;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get parsedToDate() {
    return this.toDateMoment.format('YYYY-MM-DD');
  }

  @dependentKeyCompat
  get pickup(): SegmentationValueLike[] {
    return this.model.pickup.last?.value?.pickup ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pickupAndPaceTranslation() {
    if (!this.riSelections.isCancellationsOnlyView) {
      return this.intl.t('revenue_insight.by_pickup_and_pace');
    }

    if (this.riSelections.isCancellationsAbsoluteView) {
      return this.intl.t('revenue_insight.by_recent_cancellations_and_pace');
    }

    return this.intl.t('revenue_insight.by_pace');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get range() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  get rates(): Period[] {
    return this.model.rates.last?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refDatePickerRangeOptions() {
    let { start } = this.range;
    if (!start) {
      start = this.hotelAndMarketService.getLocalTime();
    }

    const swly = start.subtract(WEEKS_IN_YEAR, 'week');
    const sdly = start.subtract(1, 'year');

    const ppStart = this.calculatePPstart(start);
    const ppEnd = ppStart.add(this.referencePeriod, 'day');

    const sdlyEnd = getSdlyEnd(start, sdly, this.referencePeriod);

    type SidebarOption = {
      disabledText?: string;
      displayRange?: boolean;
      dropdownText?: string;
      hasCalendar?: boolean;
      id: string;
      isDisabled: boolean;
      isSelected: boolean;
      range: {
        end: Dayjs | null;
        start: Dayjs | null;
      };
      text: string;
    };

    type SidebarList = {
      id: string;
      linkto?: unknown;
      options: SidebarOption[];
      title: string;
    };

    const sidebarLists: SidebarList[] = [
      {
        id: 'references',
        title: this.intl.t('datepicker.options.vs_reference').toString(),
        options: [
          {
            dropdownText: 'revenue_insight.swly_short',
            text: 'revenue_insight.swly_long',
            id: 'SWLY',
            isSelected: true,
            hasCalendar: true,
            displayRange: false,
            isDisabled: false,
            range: {
              start: swly,
              end: swly.add(this.referencePeriod, 'day'),
            },
          },
          {
            dropdownText: 'revenue_insight.sdly_short',
            text: 'revenue_insight.sdly_long',
            id: 'SDLY',
            isSelected: false,
            hasCalendar: true,
            displayRange: false,
            isDisabled: false,
            range: {
              start: sdly,
              end: sdlyEnd,
            },
          },
          {
            dropdownText: tKey('revenue_insight.legend.previous_period'),
            text: tKey('revenue_insight.legend.previous_period'),
            id: 'PP',
            isSelected: false,
            hasCalendar: true,
            displayRange: true,
            isDisabled: false,
            range: {
              start: ppStart,
              end: ppEnd,
            },
          },
          {
            text: 'general.custom',
            id: 'custom',
            displayRange: true,
            isSelected: false,
            hasCalendar: true,
            isDisabled: false,
            range: {
              start: this.refFromDateMoment,
              end: this.refFromDateMoment.add(this.referencePeriod, 'day'),
            },
          },
        ],
      },
    ];

    let linkTo;
    if (this.access.hasBudgetAndForecastSettings) {
      linkTo = {
        title: this.intl.t('settings.add_edit_targets'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: (selectedOption: { id: string }) => {
          const route =
            selectedOption.id === 'forecast'
              ? 'hotel.settings.forecast'
              : 'hotel.settings.budget';
          this.router.transitionTo(route);
        },
      };
    } else if (this.access.hasBudgetAndForecast) {
      linkTo = {
        title: this.intl.t('settings.view_targets'),
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        onClick: (selectedOption: { id: string }) => {
          const route =
            selectedOption.id === 'forecast'
              ? 'hotel.forecast'
              : 'hotel.budget';
          this.router.transitionTo(route);
        },
      };
    }

    sidebarLists.push({
      id: 'targets',
      title: this.intl.t('datepicker.options.vs_target').toString(),
      linkto: linkTo,
      options: [
        {
          dropdownText: 'revenue_insight.budget',
          text: 'revenue_insight.budget',
          id: 'budget',
          isSelected: false,
          isDisabled: this.riSelections.isCancellationsOnlyView,
          disabledText: this.intl.t(
            'revenue_insight.comparison.target.not_available',
          ),
          // We solely apply a range pointing to the current month here
          // Because this puts the calendar to a place grayed out in the calendar, indicating it's disabled...
          // For budget, the range does not matter anyway
          range: {
            start: null,
            end: null,
          },
        },
        {
          dropdownText: 'general.forecast',
          text: 'general.forecast',
          id: 'forecast',
          isSelected: false,
          isDisabled: this.riSelections.isCancellationsOnlyView,
          disabledText: this.intl.t(
            'revenue_insight.comparison.target.not_available',
          ),
          // We solely apply a range pointing to the current month here
          // Because this puts the calendar to a place grayed out in the calendar, indicating it's disabled...
          // For forecast, the range does not matter anyway
          range: {
            start: null,
            end: null,
          },
        },
      ],
    });

    return sidebarLists;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get referencePeriod() {
    if (this.fromDateMoment && this.toDateMoment) {
      return this.toDateMoment.diff(this.fromDateMoment, 'day');
    }
    return 0;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refFromDateMoment() {
    // Subtracting WEEKS_IN_YEAR gives us SWLY
    return this.refFromDate
      ? this.hotelAndMarketService.toLocalTime(this.refFromDate)
      : this.hotelAndMarketService
          .getLocalTime()
          .startOf('month')
          .subtract(WEEKS_IN_YEAR, 'week');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refRange() {
    return {
      start: this.refFromDateMoment,
      end: this.refToDateMoment,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get refToDateMoment() {
    const { fromDateMoment, refFromDateMoment, referencePeriod, reference } =
      this;

    if (referencePeriod >= 0) {
      if (reference === 'SDLY') {
        // Only remove a day when SDLY is selected
        return getSdlyEnd(
          fromDateMoment,
          refFromDateMoment,
          this.referencePeriod,
        );
      }
      return refFromDateMoment.add(referencePeriod, 'day');
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get riCurrency() {
    return this.hotelAndMarketService.hotelOrMarket?.revenueInsightCurrency;
  }

  /**
   * Determines order of the segmentation blocks
   * Based on the order available on the hotel object
   * @return { array }  : array of object which contain the key and whether they are visible. The array is in the order in which the blocks should appear
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationBlocksOrder() {
    const userSegmentationOrder = this.segmentationOrder;
    const userSegmentationCategoryMappings = this.segmentationCategoryMappings;

    // Copy the default (this is an object, so modifying would modify the default, which we do not want)
    const defaultBlocksCopy = cloneDeep(
      DEFAULT_SEGMENTATION_BLOCKS_ORDER,
    ) as SegmentationBlock[];

    // Hide non-supported breakdowns for the current subscription
    defaultBlocksCopy.forEach((segmentation) => {
      segmentation.isSupported = this.isSegmentationSupported(segmentation);
    });

    // if there is no order yet, simply display the default
    if (!userSegmentationOrder || userSegmentationOrder.length === 0) {
      return defaultBlocksCopy;
    }

    // create two arrays up front, this will enable us to get the correct order of the invisibleSegmentation by just filtering
    // the order as in the current array should be kept
    // The visible segmentations will have to be filtered again
    const visibleSegmentations: SegmentationBlock[] = [];
    const invisibleSegmentations: SegmentationBlock[] = [];

    defaultBlocksCopy.forEach((segmentation) => {
      const index = userSegmentationOrder.indexOf(segmentation.segmentationKey);
      if (index !== -1) {
        // Saving this index on the object will enable us to sort the items, without looping over them again
        segmentation.userSegmentationOrderIndex = index;
        segmentation.isVisible = true;
        visibleSegmentations.push(segmentation);
      } else {
        // Need to set this to invisible (visible by default)
        segmentation.isVisible = false;
        invisibleSegmentations.push(segmentation);
      }
      segmentation.isMappingVisible =
        userSegmentationCategoryMappings.indexOf(
          segmentation.segmentationKey,
        ) !== -1;
    });

    // Now we still need to sort the visible segmentations in the order provided by the API
    // And concat the invisible ones afterwards
    const result = visibleSegmentations
      .sort(
        (a, b) =>
          (a.userSegmentationOrderIndex ?? 0) -
          (b.userSegmentationOrderIndex ?? 0),
      )
      .concat(invisibleSegmentations);

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationCategoryMappings() {
    return this.hotelAndMarketService.user?.segmentationCategoryMappings;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get segmentationOrder() {
    return this.hotelAndMarketService.user?.segmentationOrder;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedStayDateAggregationInDropdown() {
    return this.stayDateAggregationOptions.find(
      (category) => category.id === this.stayDateAggregation,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldShowPaceEmptyState() {
    return !this.pace.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get shouldShowPickupAndPaceInaccurateDataState() {
    return this.pickup.some(
      (segmentation) =>
        segmentation.value?.message === INVALID_RESERVATIONS_MESSAGE ||
        segmentation.referenceValue?.message === INVALID_RESERVATIONS_MESSAGE,
    );
  }

  // Show the pace placeholder during a model-wide load (initial or
  // filter/date/reference change); a pace-only refetch keeps the previous chart.
  get showPaceLoadingSkeleton(): boolean {
    return (
      this.model.pace.isRunning &&
      (!this.riSelections.isPaceOnlyLoad || !this.model.pace.lastSuccessful)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPickupLY() {
    return this.riSelections.showPickupLY;
  }

  // Hidden when filtering on Pick-up or on Pace excluding future dates; else shown.
  get showPickupRealisedToggle(): boolean {
    // Budget/forecast uses the target-picker checkbox, not this toggle.
    if (this.riSelections.isTargetActive) {
      return false;
    }
    if (this.isPickupFilterActive) {
      return false;
    }
    if (this.isPaceFilterActive) {
      return this.paceFilterIncludesFutureDates;
    }
    return true;
  }

  // Segmentation block expects sorted segmentations, so we'll need to sort this as well
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedSegmentationPickup() {
    const segmentationPickup = createSegmentationBlockStructure(
      RI_BREAKDOWNS.PICKUP,
      this.pickup,
    );
    return this.mapSortableSegmentation(
      segmentationPickup,
      this.extraSortOptions,
    );
  }

  @computed(
    'breakdowns',
    'extraSortOptions',
    'isModelLoading',
    'kpi',
    'riSelections.{isBudgetActive,isForecastActive}',
    'visibleSegmentationIds.[]',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sortedVisibleSegmentations() {
    const groupedSegmentations: Partial<
      Record<RiBreakdown, ReturnType<typeof this.mapSortableSegmentation>>
    > = {};
    // Creating an array of objects containing the segmentation objects grouped by segmentation
    // We add them in the order indicated in the segmentationBlocksOrder array
    this.visibleSegmentationIds
      .filter(
        (segmentationKey) =>
          !(DEFAULT_BREAKDOWNS as RiBreakdown[]).includes(segmentationKey),
      )
      .forEach((segmentationKey) => {
        const segmentationBlockStructure = createSegmentationBlockStructure(
          segmentationKey,
          this.breakdowns?.[segmentationKey] ?? [],
        );
        groupedSegmentations[segmentationKey] = this.mapSortableSegmentation(
          segmentationBlockStructure,
          this.extraSortOptions,
        );
      });

    return groupedSegmentations;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get stayDateAggregationOptions() {
    return [
      { id: 'day', label: this.intl.t('general.day').toString() },
      { id: 'week', label: this.intl.t('general.week').toString() },
      { id: 'month', label: this.intl.t('general.month').toString() },
    ];
  }

  @computed('kpis.impact_date', 'mappedRates', 'model.kpis.isRunning')
  get stayDateData(): SegmentationValueLike[] {
    if (!this.model.kpis.isRunning) {
      const stayDateData = this.kpis[RI_BREAKDOWNS.IMPACT_DATE] ?? [];
      const completeStayDateData = addRatesToStayDates(
        stayDateData,
        this.mappedRates,
      );

      return completeStayDateData;
    }

    return [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get toDateMoment() {
    return this.toDate
      ? this.hotelAndMarketService.toLocalTime(this.toDate)
      : this.hotelAndMarketService.getLocalTime().endOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unavailableFiltersForCurrentView() {
    const unavailableFilters = [];

    if (!this.riSelections.isCancellationsOnlyView) {
      unavailableFilters.push(RI_BREAKDOWNS.CANCELLATION_LEAD_TIME);
    }

    if (this.riSelections.isCancellationsPercentageView) {
      unavailableFilters.push(RI_BREAKDOWNS.PACE, RI_BREAKDOWNS.PICKUP);
    }

    return unavailableFilters;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get updateTimestamp() {
    if (this.isDemoProperty) {
      // Fixed dummy updateTimestamp of "6 hours ago"
      return this.hotelAndMarketService.getLocalTime().subtract(6, 'hour');
    }

    if (this.isClosed) {
      return this.hotel?.revenueInsightClosureDate;
    }

    if (
      'pmsIntegrationLastUpdate' in this.hotelOrMarket &&
      this.hotelOrMarket.pmsIntegrationLastUpdate
    ) {
      return this.hotelOrMarket.pmsIntegrationLastUpdate;
    }

    return null;
  }

  // Returns all of the KPIs of the active tabs in the KPI blocks.
  // In some breakdowns we show different visible KPIs, for those we replace them.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleKpisPerBreakdown() {
    const visibleKpisPerBreakdown: Partial<Record<RiBreakdown, ValidKpi[]>> =
      {};

    const visibleKpis = this.kpiBlocks.map((kpiBlock) => kpiBlock.activeTab);
    const replacedVisibleKpis = this.kpiBlocks.map(
      (kpiBlock) =>
        KPIS_TO_REPLACE[kpiBlock.activeTab as keyof typeof KPIS_TO_REPLACE] ??
        kpiBlock.activeTab,
    );

    FILTERABLE_SEGMENTATION_BLOCKS.forEach((blockId) => {
      visibleKpisPerBreakdown[blockId] = replacedVisibleKpis;
    });

    visibleKpisPerBreakdown[RI_BREAKDOWNS.ALL] = visibleKpis;
    visibleKpisPerBreakdown[RI_BREAKDOWNS.IMPACT_DATE] = visibleKpis;
    visibleKpisPerBreakdown[RI_BREAKDOWNS.SUBSCRIPTION_ID] = visibleKpis;

    return visibleKpisPerBreakdown;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleSegmentationIds() {
    // Get all segmentations that should be shown
    let segmentationBlocksOrder = this.segmentationBlocksOrder.filter(
      (block) => block.isVisible,
    );

    segmentationBlocksOrder = this.filterVisibleSegmentations(
      segmentationBlocksOrder,
    );

    return segmentationBlocksOrder.map(
      (segmentationBlock) => segmentationBlock.segmentationKey,
    );
  }

  constructor() {
    // eslint-disable-next-line prefer-rest-params
    super(...arguments);

    this.exportChoices = [];

    if (Cookies.get(MESSAGE_DISPLAY_COOKIE)) {
      this.doNotDisplayRIFilterMessage = true;
    }

    if (Cookies.get(NET_BOOKINGS_DISPLAY_COOKIE)) {
      this.doNotDisplayNetBookingsInformationBanner = true;
    }

    if (Cookies.get(NET_CANCELLATIONS_DISPLAY_COOKIE)) {
      this.doNotDisplayNetCancellationsInformationBanner = true;
    }

    // Make a default empty search state for all blocks where you can filter
    this.searchValuesPerSegmentationId = FILTERABLE_SEGMENTATION_BLOCKS.reduce(
      (acc, segmentationId) => {
        acc[segmentationId] = {
          searchValue: '',
          isSearchActive: false,
        };
        return acc;
      },
      {} as typeof this.searchValuesPerSegmentationId,
    );
  }

  // Creates a dict with a mapping per breakdown of segmentation_value_ids to their respective segmentation_values
  // e.g. {'parent-travel-agent-...': 'Travel agent', ''}
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  buildSegmentDisplayNameOverrideForBreakdown(
    breakdown: RiBreakdown,
    segmentationValueIds: string[],
  ) {
    const segmentationDisplayNameOverride: Record<string, string> = {};

    segmentationValueIds.forEach((segmentationValueId) => {
      const segmentationItem = this.findSegmentationItemForValueId(
        this.breakdowns[breakdown] ?? [],
        segmentationValueId,
      );

      // Check if the segmentationItem was found, it can be empty for the initial page load when the API response is not returned
      if (segmentationItem && segmentationItem.segmentationValue) {
        segmentationDisplayNameOverride[segmentationValueId] =
          segmentationItem.segmentationValue;
      }
    });

    return segmentationDisplayNameOverride;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculatePPstart(start: Dayjs) {
    const startingDay = start.isoWeekday();
    const previousPeriodStart = start.subtract(this.referencePeriod, 'day');
    if (startingDay < previousPeriodStart.isoWeekday()) {
      return previousPeriodStart.isoWeekday(startingDay);
    }
    return previousPeriodStart.subtract(7, 'day').isoWeekday(startingDay);
  }

  /**
   * This check will compare the available segmentationFilters with the user visible blocks
   * If there are any filters present that are not visible for the user, we will show a warning
   * The user then has the option to add the missing blocks
   * These will be added at the end of the block
   * @param  { string } segmentationFilters : A string representation of the fitlers, will be the empty string if no fitlers are present
   */

  async checkForMissingBlocks(
    segmentationFilters: string,
    displayMessage = false,
  ): Promise<void> {
    const userSegmentationFilters = this.segmentationOrder;

    // Don't need to do anything if there are no filters, or if you do not want to see this message or no custom userSegmentationFilters (empty array = show everything)
    if (segmentationFilters && userSegmentationFilters.length) {
      // First parse the filters
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parsedSegmentationFilters = JSON.parse(segmentationFilters);
      const missingSegmentations = Object.keys(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        parsedSegmentationFilters,
      ).filter(
        // Loop over the filters and select those that are missing
        (filter) =>
          userSegmentationFilters.indexOf(filter) === -1 &&
          (ALL_SELECTABLE_SEGMENTATION_BLOCKS as string[]).indexOf(filter) !==
            -1,
      );

      // If there are missing filters, show the error message
      if (missingSegmentations.length > 0) {
        if (displayMessage) {
          const content = this.createFilterMessageContent(missingSegmentations);

          const addMissingBlocks = await this.alerting.showWarningAlert({
            title: this.intl.t('revenue_insight.missing_block.title', {
              count: missingSegmentations.length,
            }),
            htmlContent: content,
            buttons: {
              cancel: {
                text: this.intl.t('general.cancel'),
              },
              confirm: {
                text: this.intl.t('revenue_insight.missing_block.add_button', {
                  count: missingSegmentations.length,
                }),
              },
            },
          });
          if (addMissingBlocks) {
            const newSegmentations =
              userSegmentationFilters.concat(missingSegmentations);
            const data = {
              user: {
                segmentations: newSegmentations,
              },
            };
            this.ajax
              .patch('/user/segmentations/', {
                data: JSON.stringify(data),
                contentType: 'application/json',
              })
              .then((payload) => {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                this.store.pushPayload(payload);
              });
          } else {
            // if he does not want to add the missing blocks, we remove the filters
            this.removeFiltersForHiddenBlocks(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              parsedSegmentationFilters,
              missingSegmentations,
            );
          }
        } else {
          // if he does not want ot see the message, we remove the filters
          this.removeFiltersForHiddenBlocks(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            parsedSegmentationFilters,
            missingSegmentations,
          );
        }
      }
    }
  }

  checkForUnavailableBlocks(): void {
    const unavailableSegmentations = Object.keys(
      this.parsedSegmentationFilters,
    ).filter(
      // Loop over the filters and select those that are unavailable
      (filter) => this.unavailableFiltersForCurrentView.indexOf(filter) !== -1,
    );

    if (unavailableSegmentations.length > 0) {
      this.removeFiltersForHiddenBlocks(
        this.parsedSegmentationFilters,
        unavailableSegmentations,
      );
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.showUnavailableMessageTask.perform();
    }
  }

  /**
   * Creates the content for the alert if there are missing blocks
   * Need to create the content manually, since the alerting service expects it this way
   * We use the translation HTML as a basis and a a checkbox to it
   * We add the checkbox here since we need the eventlistener for the click
   * @param  { Array } missingSegmentations : array of missing segmentation ids
   * @return { DOMNode }                             : Node containing the HTML to be displayed in the message
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  createFilterMessageContent(missingSegmentations: string[]) {
    // create the container div
    const content = document.createElement('div');
    // create the container for the chcekbox
    const checkboxContainer = document.createElement('div');
    // Add a class for the checkbox, allows for easier styling
    checkboxContainer.className = 'prism-alert-content-checkbox-container';
    // Actual checkbox
    checkboxContainer.innerHTML = `
      <input
        type="checkbox"
        id="do-not-track-checkbox"
        name="do-not-track-checkbox"
      />
      <label class="text-300--regular" for="do-not-track-checkbox">
        ${this.intl.t('general.do_not_show_message')}
      </label>
      `;

    checkboxContainer.firstChild?.addEventListener('change', () => {
      // Set the property
      this.doNotDisplayRIFilterMessage = !this.doNotDisplayRIFilterMessage;
      // Set the cookie
      toggleFilterMessageCookie(this.doNotDisplayRIFilterMessage);
    });

    const mappedMissingSegmentations = missingSegmentations.map(
      // map each filter to a HTML representation that will be used in the error message
      (missingFilter) => `
      <li>
        ${this.intl.t(`revenue_insight.${missingFilter}`).toString()}
      </li>
      `,
    );

    let learnMoreHTML = '';

    // We need the form for redirecting to tripadvisor help
    // We can't do that from the alerting since we can't couple an action from here
    // So we won't show the link there
    if (!this.hotel.isTripadvisorRebranding) {
      learnMoreHTML = `
        <p class="text-300--regular">
          <a
            target="_blank"
            href="https://help.${
              DOMAIN_NAME
            }/en/articles/1789841-revenue-insight-how-to-reposition-or-hide-breakdowns"
          >
            ${this.intl.t('revenue_insight.missing_block.content_learn_more')}
          </a>
        </p>
      `;
    }

    // Count will handle pluralization of the error message
    // blocks is the joined string of the missing blocks, note that this needs to be wrapped in a htmlSafe to be shown as HTMl, otherwise it will be escaped

    /* eslint-disable @typescript-eslint/restrict-template-expressions */
    content.innerHTML = `
      <p class="paragraph-300--regular">
        ${this.intl
          .t('revenue_insight.missing_block.content_list', {
            count: mappedMissingSegmentations.length,
          })
          .toString()}
      </p>
      <ul class="revenue-insight-hidden-blocks text-300--regular">
        ${htmlSafe(mappedMissingSegmentations.join(''))}
      </ul>
      <p class="paragraph-300--regular">
        ${this.intl.t('revenue_insight.missing_block.content_unhide')}
      </p>
      ${learnMoreHTML}
      `;
    /* eslint-next @typescript-eslint/restrict-template-expressions */

    content.appendChild(checkboxContainer);

    return content;
  }

  // Remove the parent from filters and add all of its children to the filters.

  static deselectParentAndSelectChildren(
    parent: SegmentationValueLike,
    currentFilters: ParsedFilters,
  ): void {
    if (
      !parent.segmentationId ||
      !parent.segmentationValueId ||
      !parent.children
    ) {
      return;
    }

    removeFilter(
      parent.segmentationId,
      parent.segmentationValueId,
      currentFilters,
    );
    addFilters(parent.children, currentFilters);
  }

  /**
   * Opens export URLs for the admin specific debugging tools
   * @param  { string } host  : base backend hostname
   * @param  { string } path  : api endpoint path
   */

  exportBookedVsStayedData(): void {
    const params = {
      subscription_id: this.hotelOrMarket.id,
      start_date: this.fromDateMoment.format('YYYY-MM-DD'),
      end_date: this.toDateMoment.format('YYYY-MM-DD'),
    };
    const url = formatUrl(
      '/apigateway/v1/app/download/revenueinsight/booked-vs-stayed',
      params,
      this.ajax.host,
    );
    openUrl(url);
  }

  exportDataForDebugging(path: string): void {
    const ourIntegrationId = this.hotel?.ourIntegrationId;
    if (!ourIntegrationId) {
      // Hotel has no PMS integration — opening the debug tool would result in an error.
      return;
    }

    const debugToolsExportURL = generateSingleURL(
      ENV.deToolsURL,
      path,
      ourIntegrationId,
      this.formattedExtractDate,
      this.breakdowns,
      this.segmentationCategoryMappings,
      this.parsedFromDate,
      this.parsedToDate,
      {
        reference: this.reference,
        segmentationFilters: this.segmentationFilters,
        // showPickupLY is context-aware, so correct for every reference period.
        disablePickupLy: !this.showPickupLY,
        kpiCategory: this.kpiCategory,
      },
      Boolean(this.segmentationFilters),
    );

    openUrl(debugToolsExportURL);
  }

  /**
   * Calls the export endpoint
   * Can do this for a specific segment
   * This is a function so that we can use it in the breakdown-detail controller
   * @param  { string } segment : id of a specific breakdown
   */

  exportSegmentData(segment?: RiBreakdown): void {
    const baseParams = this.getBaseExcelExportParams(segment);
    const extraParams = this.getExtraExcelExportParams();
    const params = Object.assign(baseParams, extraParams) as Record<
      string,
      unknown
    >;

    if (params['roomsTab'] === KPIS.NR_OF_ROOMS) {
      params['roomsTab'] = 'nr_of_rooms';
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/revenueinsight',
      params,
      this.ajax.host,
    );

    openUrl(url);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  filterVisibleSegmentations(segmentationBlocksOrder: SegmentationBlock[]) {
    let supportedSegmentationBlocksOrder = segmentationBlocksOrder;

    if (this.riSelections.isSingleProperty) {
      supportedSegmentationBlocksOrder =
        supportedSegmentationBlocksOrder.filter(
          (block) =>
            this.hotel.revenueInsightSupportedAggregations.indexOf(
              block.segmentationKey,
            ) !== -1,
        );
    }

    if (!this.riSelections.isCancellationsOnlyView) {
      supportedSegmentationBlocksOrder =
        supportedSegmentationBlocksOrder.filter(
          (block) =>
            (CANCELLATION_ONLY_BLOCKS as RiBreakdown[]).indexOf(
              block.segmentationKey,
            ) === -1,
        );
    }

    return this.riSelections.isTargetActive
      ? supportedSegmentationBlocksOrder.filter(
          (block) =>
            (TARGET_BLOCKS as RiBreakdown[]).indexOf(block.segmentationKey) !==
            -1,
        )
      : supportedSegmentationBlocksOrder;
  }

  // Recursively finds the segmentation item for a given segmentation value id in a list of items and their children
  findSegmentationItemForValueId(
    segmentationItems: SegmentationValueLike[],
    segmentationValueId: string,
  ): SegmentationValueLike | null {
    for (let i = 0; i < segmentationItems.length; i++) {
      const segmentationItem = segmentationItems[i] as SegmentationValueLike;
      // Return if the current item matches
      if (segmentationItem.segmentationValueId === segmentationValueId) {
        return segmentationItem;
      }
      // Otherwise recursively check if it's one of the children
      if (segmentationItem.children) {
        const childItem = this.findSegmentationItemForValueId(
          segmentationItem.children,
          segmentationValueId,
        );
        if (childItem) {
          return childItem;
        }
      }
    }
    return null;
  }

  // Returns all the nested children (children, grandchildren, and so on) of a segmentation object in an array.
  static getAllNestedChildren(
    segmentationObject: SegmentationValueLike,
  ): SegmentationValueLike[] {
    const offspring: SegmentationValueLike[] = [];

    if (segmentationObject.children) {
      segmentationObject.children.forEach((child) => {
        offspring.push(child);

        offspring.push(
          ...HotelRevenueInsightController.getAllNestedChildren(child),
        );
      });
    }

    return offspring;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getBaseExcelExportParams(segment?: RiBreakdown) {
    let fromDate = this.parsedFromDate;
    let toDate = this.parsedToDate;

    if (this.stayDateFilter) {
      const { parsedStayDateFilter } = this;
      if (parsedStayDateFilter?.startDate && parsedStayDateFilter?.endDate) {
        fromDate = parsedStayDateFilter.startDate;
        toDate = parsedStayDateFilter.endDate;
      }
    }

    // Take the stay date filter offset into account for the export
    const { stayDateFilterOffset } = getDateRange(
      this.parsedFromDate,
      this.parsedToDate,
      this.hotelAndMarketService,
      this.stayDateFilter,
    );

    const params: Record<string, unknown> = {
      fromDate,
      toDate,
      refFromDate: this.refRange.start

        .add(stayDateFilterOffset, 'day')
        .format('YYYY-MM-DD'),
      segmentationFilters: this.segmentationFilters,
      refPeriod: this.reference === 'PP' ? 'custom' : this.reference,
      stay_date_aggregation: this.stayDateAggregation,
      disable_pickup_ly: !this.showPickupLY,
      currency: this.riSelections.riCurrency,
      leadtime: this.leadtime,
    };

    if (this.profile) {
      params['profile'] = this.profile;
    }

    if (segment) {
      params['segment'] = segment;
    }

    const categoryAggregations = this.segmentationCategoryMappings.filter(
      (category) => category !== 'isLoading',
    );
    if (categoryAggregations) {
      params['category_aggregations[]'] = categoryAggregations;
    }

    return params;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getExtraExcelExportParams() {
    const kpiCategories =
      this.hotel.revenueInsightKpiDisplay === KPI_DISPLAY_CUSTOM_MAPPING
        ? [this.kpiCategory]
        : this.riSelections.availableKpiCategories;

    const visibleKpisParams = {
      revenueTab: this.revenueTab,
      roomsTab: this.roomsTab,
    };

    return {
      hotel_id: this.hotelOrMarket.id,
      view: this.selectedView,
      'kpi_categories[]': kpiCategories,
      ...visibleKpisParams,
    };
  }

  // Note: disabled the eslint rule because the solution is making this a static function, but then we can't overwrite it in MP.

  handleActiveParent(
    activeParents: SegmentationValueLike[],
    currentFilters: ParsedFilters,
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    // For each active parent (in the order of top to bottom): remove the parent from the filters, and add it's children to the filters instead.
    activeParents.forEach((activeParent) => {
      HotelRevenueInsightController.deselectParentAndSelectChildren(
        activeParent,
        currentFilters,
      );
    });

    // Remove the selected filter.
    removeFilter(segmentationId, segmentationValueId, currentFilters);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isSegmentationSupported(segmentation: SegmentationBlock) {
    return this.hotel.revenueInsightSupportedAggregations.includes(
      segmentation.segmentationKey,
    );
  }

  mapSortableSegmentation(
    segmentation: SegmentationBlockStructure,
    extraOptions: { sortType: string },
  ): {
    kpi: ValidKpi;
    segmentationName: RiBreakdown;
    sortedSegmentationObjects: SegmentationValueLike[];
  } {
    const { segmentationName, segmentationObjects } = segmentation;

    const sortedSegmentationObjects = sortValues(
      segmentationObjects,
      segmentationName,
      this.kpiPerBreakdown[segmentationName],
      this.parsedSegmentationFilters,
      extraOptions,
    );

    return {
      segmentationName,
      sortedSegmentationObjects,
      kpi: this.kpi,
    };
  }

  openPmsDashboard(): void {
    const ourIntegrationId = this.hotel?.ourIntegrationId;
    if (!ourIntegrationId) {
      // Hotel has no PMS integration — skip silently.
      return;
    }

    const url = formatUrl(
      `/new/${ourIntegrationId}/`,
      {
        extract_date: this.formattedExtractDate,
        comp_start_date: this.parsedFromDate,
        comp_end_date: this.parsedToDate,
      },
      ENV.pmsDashboardURL,
    );
    openUrl(url);
  }

  /**
   * If he has hidden a block where filters are active for, these need to be removed
   * @param { object} parsedSegmentationFilters : segmentation filters as an object
   * @param { array } missingSegmentations      : array of missing segmentation filters
   * @param { object } transition
   * @param { objects } params                  : all params
   */

  removeFiltersForHiddenBlocks(
    parsedSegmentationFilters: ParsedFilters,
    missingSegmentations: string[],
  ): void {
    missingSegmentations.forEach((segmentation) => {
      delete parsedSegmentationFilters[segmentation];
    });

    if (Object.keys(parsedSegmentationFilters).length === 0) {
      this.setSegmentationFilters('');
    } else {
      this.setSegmentationFilters(JSON.stringify(parsedSegmentationFilters));
    }
  }

  setSegmentationFilters(filters: string): void {
    this.segmentationFilters = filters;
  }

  setStayDateFilter(filters: string): void {
    this.stayDateFilter = filters;
  }

  @action breakdownSettingsOptionsChanged(optionsChoice: Option): void {
    if (optionsChoice['id'] === SETTINGS_OPTIONS.BREAKDOWN_SETTINGS) {
      this.openSelectionAndSortModal();
    } else if (optionsChoice['id'] === SETTINGS_OPTIONS.SEGMENTATION_VIEW) {
      this.isShowingReviMappingTypeModal = true;
    } else if (optionsChoice['id'] === SETTINGS_OPTIONS.CENTRAL_MAPPING) {
      const mappingURL = this.router.urlFor(
        'hotel.settings.central-mapping.menu',
      );

      openUrl(mappingURL);

      this.usageAnalytics.track('Mapping Page Redirected', {
        eventTrigger: 'mapping-settings',
      });
    }
  }

  @action changeSelectedKpi(kpi: ValidKpi): void {
    this.kpi = kpi;
  }

  @action changeSelectedKpiCategory(kpiCategory: KpiCategory): void {
    this.kpiCategory = kpiCategory;
  }

  @action chartSelectionChanged(
    id: RiBreakdown,
    range: { end?: Dayjs; start?: Dayjs },
  ): void {
    if (range.start && range.end) {
      // TODO: correct timezones
      const strResult = `${range.start.format('YYYY-MM-DD')}:${range.end.format('YYYY-MM-DD')}`;

      if (id === RI_BREAKDOWNS.IMPACT_DATE) {
        this.send('stayDateFilterClicked', id, strResult);
      } else if (!this.riSelections.isTargetActive) {
        this.send('segmentationFilterClicked', id, strResult);
      }
    }
  }

  @action closeScheduleModal(): void {
    this.scheduleReport = false;
  }

  /**
   * Close the modal to select visible segmentation blocks and sort them
   */
  @action closeSelectionAndSortModal(): void {
    this.isShowingSelectionAndSortModal = false;
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.checkForMissingBlocks(this.segmentationFilters);
  }

  @action exportBasicReport(): void {
    this.usageAnalytics.track('Data Exported', {
      eventTrigger: 'filter-bar',
      exportType: 'general',
      days: this.toDateMoment.diff(this.fromDateMoment, 'days'),
    });
    this.exportSegmentData();
  }

  @action filterBarSegmentationClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    if (segmentationId === RI_BREAKDOWNS.IMPACT_DATE) {
      this.send('stayDateFilterClicked', segmentationId, segmentationValueId);
    } else {
      this.send(
        'segmentationFilterClicked',
        segmentationId,
        segmentationValueId,
      );
    }
  }

  @action handleExport(exportChoice: Option, segment: RiBreakdown): void {
    if (exportChoice['id'] === 'schedule_report') {
      this.scheduleReport = true;
    } else if (exportChoice['id'] === 'excel_export') {
      const days = this.toDateMoment.diff(this.fromDateMoment, 'days');
      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'filter-bar',
        exportType: 'general',
        days,
      });

      this.exportSegmentData(segment);
    } else if (exportChoice['id'] === 'pms_dashboard') {
      this.openPmsDashboard();
    } else if (exportChoice['id'] === 'raw_data_export') {
      this.exportDataForDebugging('/api/spanner/export');
    } else if (exportChoice['id'] === 'debug_query') {
      this.exportDataForDebugging('/ui/spanner/reservations');
    } else if (exportChoice['id'] === 'booked_vs_stayed_export') {
      const days = this.toDateMoment.diff(this.fromDateMoment, 'days');
      this.usageAnalytics.track('Data Exported', {
        eventTrigger: 'filter-bar',
        exportType: 'booked_vs_stayed',
        days,
      });
      this.exportBookedVsStayedData();
    }
  }

  @action handleNetBookingsGotItClicked(isDontShowAgainChecked: boolean): void {
    this.doNotDisplayNetBookingsInformationBanner = true;

    const cookieExpiryOption = isDontShowAgainChecked ? {} : { expires: 1 };

    Cookies.set(NET_BOOKINGS_DISPLAY_COOKIE, 'true', cookieExpiryOption);
  }

  @action handleNetCancellationsGotItClicked(
    isDontShowAgainChecked: boolean,
  ): void {
    this.doNotDisplayNetCancellationsInformationBanner = true;

    const cookieExpiryOption = isDontShowAgainChecked ? {} : { expires: 1 };

    Cookies.set(NET_CANCELLATIONS_DISPLAY_COOKIE, 'true', cookieExpiryOption);
  }

  @action handleOnPageScrollEvent(event: Event): void {
    const target = event.target as HTMLElement | null;
    const { pickupPaceSectionElement } = this;

    if (!target || !pickupPaceSectionElement) {
      return;
    }

    const { scrollTop, clientHeight } = target;

    // Check if the pickupPaceSectionElement element is in view
    const elementTop = pickupPaceSectionElement.offsetTop;
    const elementHeight = pickupPaceSectionElement.offsetHeight;

    // Check if the element is in view
    const isInView =
      elementTop < scrollTop + clientHeight &&
      elementTop + elementHeight > scrollTop;

    if (
      isInView &&
      !this.hasTrackedPickupPaceSectionView &&
      !this.isModelLoading
    ) {
      this.hasTrackedPickupPaceSectionView = true;

      this.usageAnalytics.track('Pace Visualisation Viewed', {
        eventTrigger: 'pace-section-in-view',
      });
    }
  }

  @action onCloseReviMappingTypeModal(): void {
    this.isShowingReviMappingTypeModal = false;
  }

  @action openDayDetail(type: string, date: string): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.revenue-insight.detail', {
      queryParams: { type, date },
    });
  }

  /**
   * Open the modal to select visible segmentation blocks and sort them
   */
  @action openSelectionAndSortModal(): void {
    this.isShowingSelectionAndSortModal = true;
  }

  @action rangeChanged(range: { end: Dayjs | null; start: Dayjs }): void {
    this.setStayDateFilter('');
    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }
    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }

    if (this.reference === 'SWLY') {
      this.refFromDate = range.start
        .subtract(WEEKS_IN_YEAR, 'week')
        .format('YYYY-MM-DD');
    } else if (this.reference === 'SDLY') {
      this.refFromDate = range.start.subtract(1, 'year').format('YYYY-MM-DD');
    } else if (this.reference === 'PP') {
      this.refFromDate = this.calculatePPstart(range.start).format(
        'YYYY-MM-DD',
      );
    }

    // Custom logic: if selected date ranges are changed (not custom, ref dates), we need to drop active pickup & pace filters
    if (this.parsedSegmentationFilters) {
      let didUpdateFilters = false;

      if (this.parsedSegmentationFilters['pace']) {
        delete this.parsedSegmentationFilters['pace'];
        didUpdateFilters = true;
      }

      if (this.parsedSegmentationFilters['pickup']) {
        delete this.parsedSegmentationFilters['pickup'];
        didUpdateFilters = true;
      }

      if (didUpdateFilters) {
        if (Object.keys(this.parsedSegmentationFilters).length) {
          this.setSegmentationFilters(
            JSON.stringify(this.parsedSegmentationFilters),
          );
        } else {
          this.setSegmentationFilters('');
        }
      }
    }

    // Check if we need to set a new staydate aggregation
    const { start, end } = this.range;

    const rangeInDays = end.diff(start, 'day');
    // Default aggregation is daily
    let rangeAggregationPeriod: DateAggregationType = 'day';
    // Switch to monthly if the new range is over 92 days
    if (rangeInDays > 92) {
      // 92 days is +- > 3 months (a quarter)
      rangeAggregationPeriod = 'month';
    }
    this.stayDateAggregation = rangeAggregationPeriod;
  }

  @action refRangeChanged(range: { start: Dayjs }, option: string): void {
    // Reset loading state
    this.reference = option;

    if (range.start) {
      this.refFromDate = range.start.format('YYYY-MM-DD');
    }
  }

  @action refreshRoute(): void {
    this.send('doRefresh');
  }

  @action segmentationFilterClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.segmentationFilterClickedTask.perform(
      segmentationId,
      segmentationValueId,
    );
  }

  @action setExportChoices(): void {
    // exportChoices must be set in a did-insert since the values of the access service are still undefined in the constructor
    const exportChoices: Group<Option>[] = [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'excel_export',
            label: this.intl.t('revenue_insight.export.report'),
          },
        ],
      },
    ];

    if (this.access.hasFeature('revenueinsight.booked_vs_stay')) {
      exportChoices[0]!.options.push({
        id: 'booked_vs_stayed_export',
        label: this.intl.t('revenue_insight.export.booked_vs_stayed'),
        badge: this.intl.t('general.beta.2'),
        badgeType: 'info',
      });
    }

    exportChoices.push({
      groupName: this.intl.t('reports.schedule_report'),
      options: [
        {
          id: 'schedule_report',
          label: this.intl.t('revenue_insight.export.summary_report'),
          icon: 'calendar-clock',
        },
      ],
    });

    if (
      this.access.isStaffUser &&
      this.access.hasFeature('revenueinsight.debug_export') &&
      !this.hotelAndMarketService.isDemoMode()
    ) {
      // Add PMS dashboard, raw data export & debug query as extra options for staff users
      exportChoices.push({
        groupName: this.intl.t('revenue_insight.other'),
        options: [
          {
            id: 'pms_dashboard',
            label: 'PMS dashboard',
          },
          {
            id: 'raw_data_export',
            label: this.intl.t('revenue_insight.export.raw_data'),
          },
          {
            id: 'debug_query',
            label: this.intl.t('revenue_insight.debug.query'),
          },
        ],
      });
    }

    this.exportChoices = exportChoices;
  }

  // Used in filter bar for setting favourites
  @action setFilterValues(updatedFilterValues: {
    segmentationFilters: string;
    stayDateFilter: string;
  }): void {
    this.setSegmentationFilters(updatedFilterValues.segmentationFilters);
    this.setStayDateFilter(updatedFilterValues.stayDateFilter);
    // Check if it contains filters that are currently unavailbe and remove them
    this.checkForUnavailableBlocks();
  }

  @action setHidePickupLY(value: boolean): void {
    this.hidePickupLY = value;
  }

  @action setIsFilterBarHovered(isFilterBarHovered: boolean): void {
    this.isFilterBarHovered = isFilterBarHovered;
  }

  @action setIsSearchActive(
    segmentationId: FilterableSegmentationBlock,
    isSearchActive: boolean,
  ): void {
    const { searchValuesPerSegmentationId } = this;
    set(
      searchValuesPerSegmentationId[segmentationId],
      'isSearchActive',
      isSearchActive,
    );
  }

  @action setPaceLeadtime(value: number): void {
    if (value === this.leadtime) {
      return;
    }

    // Both QPs are refreshModel:false, so persist then refetch pace manually,
    // passing the live showPickupRealised (paramsFor would be stale).
    this.leadtime = value;
    this.route.refetchPace(value, this.showPickupRealised);
  }

  @action setPickupPaceSectionElement(element: HTMLElement | null): void {
    this.pickupPaceSectionElement = element;
  }

  @action setSearchValue(
    segmentationId: FilterableSegmentationBlock,
    searchValue: string,
  ): void {
    const { searchValuesPerSegmentationId } = this;
    set(
      searchValuesPerSegmentationId[segmentationId],
      'searchValue',
      searchValue,
    );
  }

  @action setShowPickupRealised(value: boolean): void {
    this.showPickupRealised = value;

    this.route.refetchPace(this.leadtime, value);
  }

  @action stayDateAggregationKeyChanged(aggregationChoice: Option): void {
    this.usageAnalytics.track('Stay Date View Changed', {
      eventTrigger: 'stay-date-chart-view',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      viewBy: aggregationChoice['id'],
    });

    set(
      this,
      'stayDateAggregation',
      aggregationChoice['id'] as DateAggregationType,
    );
  }

  @action stayDateFilterClicked(
    segmentationId: RiBreakdown,
    segmentationValueId: string,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const currentFilters = parseObject(this.stayDateFilter);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if ((currentFilters?.[segmentationId] ?? [])[0] === segmentationValueId) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      delete currentFilters[segmentationId];
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      currentFilters[segmentationId] = [segmentationValueId];
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const stringifiedFilters = stringifyObject(currentFilters);
    this.setStayDateFilter(stringifiedFilters);
  }

  @action toggleCancellationsView(value: CancellationsView): void {
    this.cancellationsView = value;
    this.checkForUnavailableBlocks();
  }

  @action updateDisplayedSegmentationFilters(): void {
    // create object with all filters. Will not be in correct order
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const filters = mergeFilters(this.segmentationFilters, this.stayDateFilter);

    // put in correct order
    const segmentationValueFormatterParams = {
      intl: this.intl,
      isFuture: this.riSelections.isToDateInFuture,
      segmentationDisplayNameOverride: this.filterDisplayNameOverride,
    };
    this.activeFilters = addFiltersToActiveFilters(
      this.activeFilters,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      filters,
      segmentationIdFormatter,
      segmentationValueFormatter,
      segmentationValueFormatterParams,
      this.riSelections,
    );
  }

  @action validateMissingAndUnavailableBlocks(): void {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    this.checkForMissingBlocks(this.segmentationFilters, true);
    this.checkForUnavailableBlocks();
  }
}
